// src/features/executive/components/FilterBar/filter-config.ts
// Which filters live behind the "ตัวกรองเพิ่มเติม" drawer per tab. Shared by
// ExecutiveFilterBar (badge count / button visibility) and FilterDrawer
// (which controls to render) so the two can never drift apart.
import type { ExecutiveFilterState, ExecutiveTab } from 'api/executive/executive.types'

export const EXECUTIVE_TAB_FILTER_KEYS: Record<ExecutiveTab, (keyof ExecutiveFilterState)[]> = {
  // Overview reuses the performance / sales / insight hero widgets, which all
  // honour these three — so they must be editable from here as well instead
  // of only via a chip set on another tab (MN-2609-1165 TC-02).
  overview: ['doctorId', 'procedureId', 'patientType'],
  performance: ['doctorId', 'procedureId', 'patientType', 'appointmentStatus'],
  account: ['txCategoryId', 'paymentType'],
  sales: ['doctorId', 'procedureId', 'patientType', 'appointmentStatus'],
  // doctorId here too: the insight cohort honours it (UR-2609-1158), so the
  // user must be able to see and clear it from this tab (MD-2609-1161).
  insight: ['doctorId', 'patientType', 'nationality', 'sourceChannel', 'promotionId'],
}

export function hasAdvancedFilters(tab: ExecutiveTab): boolean {
  return EXECUTIVE_TAB_FILTER_KEYS[tab].length > 0
}

/** Every drawer filter key across all tabs, de-duplicated. */
export const ALL_ADVANCED_FILTER_KEYS: (keyof ExecutiveFilterState)[] = Array.from(
  new Set(Object.values(EXECUTIVE_TAB_FILTER_KEYS).flat())
)

/**
 * Active advanced filters regardless of the current tab. A doctor picked on
 * the sales tab still narrows the insight tab, so the badge must count it
 * there too (MD-2609-1161).
 */
export function countActiveAdvancedFilters(filters: ExecutiveFilterState): number {
  return ALL_ADVANCED_FILTER_KEYS.filter((k) => filters[k] != null).length
}

/**
 * Keep only the ids of a single/CSV selection that exist in `available`.
 * Returns the selection untouched while `available` is empty (reference
 * data still loading) so a pending fetch never wipes the user's choice.
 */
export function pruneIdSelection(
  value: number | string | null,
  available: number[]
): number | string | null {
  if (value == null || value === '' || available.length === 0) return value
  const allowed = new Set(available)
  const kept = String(value)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '' && allowed.has(Number(s)))
  if (kept.length === 0) return null
  if (typeof value === 'number') return value
  return kept.join(',')
}

/**
 * Advanced filters each tab's widgets actually apply (mirrors what the
 * /api/executive/<tab> endpoints accept). Anything active outside this set is
 * still stored — it keeps narrowing the other tabs — but has no effect on the
 * tab being viewed, so the UI must say so instead of showing a chip that
 * silently changes nothing (UR-2609-1158: account tab, MD-2609-1161).
 */
export const EXECUTIVE_TAB_EFFECTIVE_KEYS: Record<ExecutiveTab, (keyof ExecutiveFilterState)[]> = {
  overview: ['doctorId', 'procedureId', 'txCategoryId', 'patientType', 'nationality', 'sourceChannel', 'promotionId'],
  performance: ['doctorId', 'procedureId', 'patientType', 'appointmentStatus'],
  // Account is clinic-level bookkeeping (income / expense / AR) — never per doctor.
  account: ['txCategoryId', 'paymentType'],
  sales: ['doctorId', 'procedureId', 'txCategoryId', 'patientType'],
  insight: ['doctorId', 'patientType', 'nationality', 'sourceChannel', 'promotionId'],
}

export function isFilterEffectiveOnTab(tab: ExecutiveTab, key: keyof ExecutiveFilterState): boolean {
  return EXECUTIVE_TAB_EFFECTIVE_KEYS[tab].includes(key)
}

/** Active advanced filters that the given tab ignores, in chip order. */
export function activeIneffectiveFilterKeys(
  tab: ExecutiveTab,
  filters: ExecutiveFilterState
): (keyof ExecutiveFilterState)[] {
  return ALL_ADVANCED_FILTER_KEYS.filter((k) => filters[k] != null && !isFilterEffectiveOnTab(tab, k))
}

/**
 * Branch selection → the `branchId` query value for the dentist lookup:
 * "1" for one branch, "1,4" for several (the API accepts a comma list —
 * MD-2609-1162 TC-03), undefined for all / nothing valid.
 */
export function normalizeBranchParam(branchId: number | string | null | undefined): string | undefined {
  if (branchId == null || branchId === '' || branchId === 'all') return undefined
  const ids = String(branchId)
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0)
  return ids.length > 0 ? ids.join(',') : undefined
}
